import { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { PERMISSION_OPTIONS } from '@/consts';

const SaveInternalsModuleModal = ({ show, onCancel, onSave, isLoading, errorMessages = [], defaultLabel = '' }) => {
  const [label, setLabel] = useState(defaultLabel);
  const [permissions, setPermissions] = useState('project');
  const [description, setDescription] = useState('');

  if (!show) {
    return null;
  }

  const handleSave = () => {
    if (!label.trim()) return;
    onSave({ label: label.trim(), permissions, description: description.trim() });
  };

  return (
    <dialog open={show} className="modal">
      <div className="modal-box">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-full">
            <ArrowDownTrayIcon className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-bold text-lg">Save internals as a module</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Save this module&apos;s internals as a reusable module: its parameters, the submodules it contains, how
          they&apos;re connected, and their parameters. Adding it to a pathway recreates all of it as it is right now —
          later changes here won&apos;t affect it.
        </p>
        <div className="mb-4">
          <label htmlFor="internals-module-label" className="block text-sm font-medium mb-1">
            Module name
          </label>
          <input
            id="internals-module-label"
            type="text"
            className="input input-bordered input-sm w-full"
            placeholder="e.g. GPP2 Train"
            value={label}
            onChange={e => setLabel(e.target.value)}
            disabled={isLoading}
          />
        </div>
        <div className="mb-4">
          <label htmlFor="internals-module-permissions" className="block text-sm font-medium mb-1">
            Permissions
          </label>
          <select
            id="internals-module-permissions"
            className="select select-bordered select-sm w-full"
            value={permissions}
            onChange={e => setPermissions(e.target.value)}
            disabled={isLoading}
          >
            {PERMISSION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {permissions === 'project' && (
            <p className="text-xs text-gray-500 mt-1">Visible to members of this project.</p>
          )}
        </div>
        <div className="mb-4">
          <label htmlFor="internals-module-description" className="block text-sm font-medium mb-1">
            Description <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <textarea
            id="internals-module-description"
            className="textarea textarea-bordered textarea-sm w-full"
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
            disabled={isLoading}
          />
        </div>
        {errorMessages.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-2 mb-2">
            {errorMessages.map(message => (
              <p key={message} className="text-xs text-red-700">
                {message}
              </p>
            ))}
          </div>
        )}
        <div className="modal-action">
          <button onClick={onCancel} disabled={isLoading} className="btn btn-sm btn-outline">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || !label.trim()}
            className="btn btn-sm bg-blue-500 hover:bg-blue-600 text-white border-none"
          >
            {isLoading ? <span className="loading loading-spinner loading-sm"></span> : 'Save'}
          </button>
        </div>
      </div>
      <div className="modal-backdrop bg-black/50" onClick={!isLoading ? onCancel : undefined}></div>
    </dialog>
  );
};

SaveInternalsModuleModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  defaultLabel: PropTypes.string,
};

export default SaveInternalsModuleModal;
